import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, Heart, ExternalLink, ChevronUp, ChevronDown, Volume2, VolumeX, Loader, Play, SkipForward, AlertTriangle } from 'lucide-react';

/**
 * Vertical swipe feed for Peak Moments.
 *
 * Design notes:
 * - Reaction videos are 16:9 and almost always show the reactor AND the content
 *   side-by-side, so cropping to 9:16 would cut one of them out. The video is
 *   letterboxed instead, with a blurred copy of its own thumbnail filling the
 *   rest of the viewport so it still reads as a full-bleed vertical experience.
 * - YouTube's embed supports BOTH start and end (`endSeconds`), so each slide
 *   plays only the window around the peak, then fires ENDED — which is what we
 *   use to auto-advance.
 * - Only the active slide (and its immediate neighbour) mounts a player, so we
 *   never have more than two YouTube iframes alive at once.
 */

const LEAD_IN = 3;        // start this many seconds before the peak, for context
const CLIP_LENGTH = 15;   // fallback length for moments submitted before clip length existed
const CLIP_MIN = 5;
const CLIP_MAX = 60;

const c = {
  text: '#f4f3f7',
  sub: 'rgba(255,255,255,0.72)',
  muted: 'rgba(255,255,255,0.5)',
  pink: '#ec4899',
  purple: '#8b5cf6',
};

// ── YouTube IFrame API loader (single shared promise) ────────────────────────
let ytApiPromise = null;
function loadYouTubeApi() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;

  ytApiPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === 'function') prev();
      resolve(window.YT);
    };
    if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const s = document.createElement('script');
      s.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(s);
    }
  });
  return ytApiPromise;
}

// The submitter picks how long the moment runs; we always add a short lead-in so
// the clip doesn't open mid-sentence. Older moments have no length and fall back.
const clipWindow = (moment) => {
  const peak = Math.max(0, Number(moment?.timestampSeconds) || 0);
  const raw = Number(moment?.clipDurationSeconds);
  const length = Number.isFinite(raw) ? Math.min(CLIP_MAX, Math.max(CLIP_MIN, raw)) : CLIP_LENGTH;
  const start = Math.max(0, peak - LEAD_IN);
  return { start, end: peak + length };
};

// ── One slide ────────────────────────────────────────────────────────────────

const Slide = ({ moment, isActive, muted, onEnded, onToggleMute, onLike, liked, onOpenFull, isMobile }) => {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [started, setStarted] = useState(false);
  const [showTapToPlay, setShowTapToPlay] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [progress, setProgress] = useState(0);
  const { start, end } = clipWindow(moment);
  const thumb = moment.videoThumbnail || (moment.videoId ? `https://img.youtube.com/vi/${moment.videoId}/hqdefault.jpg` : '');

  // Mount a player only while this slide is active; tear it down when it isn't,
  // so we never keep a pile of iframes alive as the user scrolls.
  useEffect(() => {
    let cancelled = false;
    if (!isActive || !moment.videoId) return undefined;
    const host = hostRef.current;
    if (!host) return undefined;

    // Build the iframe ourselves instead of letting YT.Player generate it.
    // A cross-origin iframe needs an explicit `allow="autoplay"` Permissions
    // Policy grant or the browser refuses programmatic playback outright —
    // the API's own generated iframe doesn't set it, which is what left mobile
    // stuck on a spinner. Owning the element also means YouTube never swaps out
    // a node React is tracking.
    const params = new URLSearchParams({
      enablejsapi: '1',
      autoplay: '1',
      mute: muted ? '1' : '0',
      controls: '0',
      rel: '0',
      playsinline: '1',        // or iOS hijacks playback to fullscreen
      modestbranding: '1',
      disablekb: '1',
      fs: '0',
      start: String(start),
      end: String(end),
      origin: window.location.origin,
    });

    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube-nocookie.com/embed/${moment.videoId}?${params.toString()}`;
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; clipboard-write';
    iframe.setAttribute('playsinline', '');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    iframe.style.cssText = 'width:100%;height:100%;border:0;display:block;';
    host.appendChild(iframe);

    loadYouTubeApi().then((YT) => {
      if (cancelled) return;
      // Passing an existing iframe attaches to it rather than replacing it.
      playerRef.current = new YT.Player(iframe, {
        events: {
          onReady: (e) => {
            if (cancelled) return;
            setReady(true);
            // Respect the feed's current sound choice. There used to be a 1.2s
            // "force mute and retry" fallback here, but any slide slower than
            // that to buffer tripped it — which is what silenced the audio
            // between videos. The tap-to-play overlay already covers genuinely
            // blocked autoplay, and a tap is a gesture, so it can start with sound.
            if (muted) e.target.mute(); else e.target.unMute();
            e.target.playVideo();
          },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.PLAYING) setStarted(true);
            if (e.data === window.YT.PlayerState.ENDED) onEnded();
          },
          // 101/150 = the owner disallows embedding, which is also what
          // age-restricted videos report: YouTube will not play them outside
          // youtube.com no matter who is signed in. 100 = removed or private,
          // 2/5 = bad id or player failure. None of them are recoverable here,
          // so the slide says why and moves on instead of parking the feed.
          onError: () => {
            if (cancelled) return;
            setBlocked(true);
          },
        },
      });
    });

    return () => {
      cancelled = true;
      setReady(false);
      setStarted(false);
      setShowTapToPlay(false);
      setBlocked(false);
      try { playerRef.current?.destroy(); } catch { /* already gone */ }
      playerRef.current = null;
      // Clear whatever YouTube left behind. React only manages `host` itself,
      // so wiping its children here can't desync React's tree.
      try { host.innerHTML = ''; } catch { /* detached */ }
    };
  }, [isActive, moment.videoId]);

  // Mute toggle without rebuilding the player
  useEffect(() => {
    const p = playerRef.current;
    if (!p || typeof p.mute !== 'function') return;
    try { muted ? p.mute() : p.unMute(); } catch { /* not ready yet */ }
  }, [muted]);

  // There's always a gap between "player ready" and "actually playing" while the
  // clip buffers. Offering the tap target immediately made it flash on every
  // slide that was going to autoplay fine, so give autoplay a grace period and
  // only surface the prompt if it genuinely hasn't started.
  useEffect(() => {
    if (!isActive || !ready || started || blocked) { setShowTapToPlay(false); return undefined; }
    const id = setTimeout(() => setShowTapToPlay(true), 1800);
    return () => clearTimeout(id);
  }, [isActive, ready, started, blocked]);

  // An unplayable clip shouldn't be a dead end. Pause long enough to read why,
  // then move to the next moment exactly as if this one had finished.
  useEffect(() => {
    if (!isActive || !blocked) return undefined;
    const id = setTimeout(() => onEnded(), 2200);
    return () => clearTimeout(id);
  }, [isActive, blocked, onEnded]);

  // Drive the progress bar off real playback time rather than a wall clock, so
  // it stalls honestly while the video buffers instead of racing ahead.
  useEffect(() => {
    if (!isActive || !ready) { setProgress(0); return undefined; }
    const id = setInterval(() => {
      const p = playerRef.current;
      if (!p || typeof p.getCurrentTime !== 'function') return;
      try {
        const elapsed = p.getCurrentTime() - start;
        setProgress(Math.min(1, Math.max(0, elapsed / (end - start))));
      } catch { /* player torn down mid-tick */ }
    }, 120);
    return () => clearInterval(id);
  }, [isActive, ready, start, end]);

  return (
    <section
      style={{
        position: 'relative', height: '100%', width: '100%',
        scrollSnapAlign: 'start', scrollSnapStop: 'always',
        overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#000',
      }}
    >
      {/* blurred backdrop fills the vertical space the 16:9 video can't */}
      {thumb && (
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${thumb})`, backgroundSize: 'cover', backgroundPosition: 'center',
          filter: 'blur(34px) brightness(0.4)', transform: 'scale(1.25)',
        }} />
      )}

      {/* Spoiler warning — a tag only, shown over the running clip. Nothing is
          blurred or hidden anywhere; this just announces it. */}
      {moment.isSpoiler && isActive && (
        <div style={{
          // Clears the progress bar and the slide counter that sit at the very top
          position: 'absolute', top: isMobile ? 32 : 38, left: '50%', transform: 'translateX(-50%)',
          zIndex: 5, maxWidth: 'calc(100% - 32px)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
          padding: moment.spoilerMedia?.title ? '11px 26px 12px' : '11px 24px',
          borderRadius: moment.spoilerMedia?.title ? 18 : 999,
          background: 'rgba(239,68,68,0.94)',
          boxShadow: '0 8px 26px rgba(0,0,0,0.5)',
          pointerEvents: 'none',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <AlertTriangle size={isMobile ? 18 : 20} color="#fff" />
            <span style={{ fontSize: isMobile ? 15 : 17, fontWeight: 800, color: '#fff', letterSpacing: '0.8px', whiteSpace: 'nowrap' }}>
              SPOILER ALERT
            </span>
          </span>
          {moment.spoilerMedia?.title && (
            <span style={{
              fontSize: isMobile ? 13 : 14.5, fontWeight: 600, color: 'rgba(255,255,255,0.95)',
              maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {moment.spoilerMedia.title}
              {moment.spoilerMedia.releaseYear ? ` (${moment.spoilerMedia.releaseYear})` : ''}
            </span>
          )}
        </div>
      )}

      {/* clip progress — shows how long is left before auto-advance */}
      {isActive && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 3,
          background: 'rgba(255,255,255,0.16)', zIndex: 4,
        }}>
          <div style={{
            height: '100%', width: `${progress * 100}%`,
            background: 'linear-gradient(90deg, #F97316, #EAB308)',
            transition: 'width 0.12s linear',
          }} />
        </div>
      )}

      {/* 16:9 stage — letterboxed, never cropped */}
      <div style={{
        position: 'relative', width: '100%', maxWidth: 900,
        aspectRatio: '16 / 9', background: '#000',
        boxShadow: '0 24px 70px rgba(0,0,0,0.65)',
        borderRadius: isMobile ? 0 : 14, overflow: 'hidden',
      }}>
        {isActive ? (
          <>
            <div ref={hostRef} style={{ width: '100%', height: '100%' }} />
            {!started && !showTapToPlay && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
                <Loader size={26} style={{ color: c.muted, animation: 'pmf-spin 1s linear infinite' }} />
              </div>
            )}
            {/* Mobile browsers refuse programmatic autoplay unless the play call
                happens inside a real gesture. If the clip hasn't started, put a
                tap target over it so the user's tap IS that gesture. */}
            {blocked && (
              <div style={{
                position: 'absolute', inset: 0,
                background: 'rgba(0,0,0,0.82)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 10, padding: 24, textAlign: 'center',
              }}>
                <span style={{
                  width: 54, height: 54, borderRadius: '50%', background: 'rgba(255,255,255,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <SkipForward size={24} color="#fff" />
                </span>
                <span style={{ color: '#fff', fontSize: 14.5, fontWeight: 700 }}>
                  Can't be played here
                </span>
                <span style={{ color: c.muted, fontSize: 12.5, maxWidth: 300, lineHeight: 1.45 }}>
                  YouTube blocks this video outside youtube.com. Skipping to the next moment…
                </span>
              </div>
            )}
            {showTapToPlay && !started && !blocked && (
              <div style={{
                position: 'absolute', inset: 0,
                background: 'rgba(0,0,0,0.45)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
              }}>
                <button
                  type="button"
                  onClick={() => {
                    const p = playerRef.current;
                    setShowTapToPlay(false);
                    try { p?.unMute?.(); } catch { /* not ready */ }
                    try { p?.playVideo?.(); } catch { /* not ready */ }
                  }}
                  style={{
                    border: 'none', background: 'transparent', cursor: 'pointer', padding: 0,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                  }}
                >
                  <span style={{
                    width: 64, height: 64, borderRadius: '50%',
                    background: 'linear-gradient(135deg,#F97316,#EAB308)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 10px 30px rgba(249,115,22,0.5)',
                  }}>
                    <Play size={28} fill="#100c14" color="#100c14" style={{ marginLeft: 3 }} />
                  </span>
                  <span style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>Tap to play</span>
                </button>
                {/* Escape hatch for a clip that stalls without reporting an error */}
                <button
                  type="button"
                  onClick={onEnded}
                  style={{
                    marginTop: 4, padding: '7px 16px', borderRadius: 999,
                    border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(0,0,0,0.35)',
                    color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <SkipForward size={13} /> Skip
                </button>
              </div>
            )}
          </>
        ) : (
          thumb && <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
      </div>

      {/* info + actions */}
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0,
        padding: isMobile ? '18px 16px 26px' : '22px 26px 30px',
        background: 'linear-gradient(0deg, rgba(0,0,0,0.85) 20%, transparent)',
        display: 'flex', alignItems: 'flex-end', gap: 14,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8 }}>
            {moment.channelAvatarUrl && (
              <img src={moment.channelAvatarUrl} alt="" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            )}
            <span style={{ fontSize: 13.5, fontWeight: 700, color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {moment.channelName}
            </span>
            {moment.timestampFormatted && (
              <span style={{
                fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0,
                background: 'rgba(255,255,255,0.16)', padding: '2px 8px', borderRadius: 999,
              }}>
                {moment.timestampFormatted}
              </span>
            )}
            {isActive && ready && (
              <span style={{ fontSize: 11, fontWeight: 700, color: '#EAB308', flexShrink: 0 }}>
                {Math.max(0, Math.ceil((1 - progress) * (end - start)))}s
              </span>
            )}
          </div>

          {/* The creator's actual YouTube title leads. YouTube's terms require the
              source video be identified accurately, and it stops the community
              caption below from being mistaken for the real video title. */}
          <p style={{
            margin: 0, fontSize: isMobile ? 14 : 15.5, fontWeight: 700, color: c.text, lineHeight: 1.35,
            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {moment.videoTitle}
          </p>
          {moment.title && (
            <p style={{
              margin: '5px 0 0', fontSize: 12.5, color: c.sub, fontStyle: 'italic', lineHeight: 1.35,
              overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            }}>
              “{moment.title}”
            </p>
          )}

          <button
            type="button"
            onClick={() => onOpenFull(moment)}
            style={{
              marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.22)',
              color: '#fff', borderRadius: 999, padding: '7px 14px',
              fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
            }}
          >
            <ExternalLink size={13} /> Watch full reaction
          </button>
        </div>

        {/* right-hand action rail */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center', flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => onLike(moment)}
            title="Like"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: liked ? c.pink : '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}
          >
            <Heart size={26} fill={liked ? c.pink : 'none'} />
            <span style={{ fontSize: 11.5, fontWeight: 700 }}>{moment.likes || 0}</span>
          </button>

          <button
            type="button"
            onClick={onToggleMute}
            title={muted ? 'Unmute' : 'Mute'}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', display: 'flex' }}
          >
            {muted ? <VolumeX size={24} /> : <Volume2 size={24} />}
          </button>
        </div>
      </div>
    </section>
  );
};

// ── Feed ─────────────────────────────────────────────────────────────────────

/**
 * `moments` is whatever set should play — all of them, one category, one movie,
 * one scene. `startId` says which of those to open on. Entry is expressed as an
 * id rather than a position so callers never have to track indices into a list
 * whose shape may change (or be a filtered subset).
 */
const PeakMomentsFeed = ({ moments = [], startId = null, startIndex = 0, onClose, onLike, likedIds = new Set(), onOpenFull }) => {
  const scrollRef = useRef(null);
  const slideRefs = useRef([]);

  // Resolve the entry point once — later re-renders must not yank the user
  // back to where they started.
  const startAtRef = useRef(-1);
  if (startAtRef.current < 0) {
    let i = Math.min(Math.max(0, startIndex), Math.max(0, moments.length - 1));
    if (startId != null) {
      const found = moments.findIndex((m) => (m._id || m.id) === startId);
      if (found >= 0) i = found;
    }
    startAtRef.current = i;
  }

  const [active, setActive] = useState(startAtRef.current);
  // Always start with sound. Where a browser refuses unmuted autoplay the
  // tap-to-play overlay takes over, and that tap is a real user gesture, so
  // playback still begins unmuted rather than silently.
  const [muted, setMuted] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Jump to the moment the user actually clicked
  useEffect(() => {
    const el = slideRefs.current[startAtRef.current];
    if (el) el.scrollIntoView({ block: 'start' });
  }, []);

  // Track which slide is on screen
  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio > 0.6) {
            const i = slideRefs.current.indexOf(e.target);
            if (i >= 0) setActive(i);
          }
        });
      },
      { root, threshold: [0.6] }
    );
    slideRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [moments.length]);

  const goTo = useCallback((i) => {
    const el = slideRefs.current[i];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleEnded = useCallback(() => {
    setActive((cur) => {
      const next = cur + 1;
      if (next < moments.length) goTo(next);
      return cur; // IntersectionObserver sets the real value once scrolled
    });
  }, [moments.length, goTo]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowDown') { e.preventDefault(); goTo(Math.min(active + 1, moments.length - 1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); goTo(Math.max(active - 1, 0)); }
      if (e.key === 'm' || e.key === 'M') setMuted((m) => !m);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, moments.length, goTo, onClose]);

  if (!moments.length) return null;

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: '#000' }}>
      <style>{`
        @keyframes pmf-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .pmf-scroll::-webkit-scrollbar { display: none; }
      `}</style>

      <button
        type="button"
        onClick={onClose}
        style={{
          position: 'absolute', top: 16, right: 16, zIndex: 3,
          width: 38, height: 38, borderRadius: '50%',
          background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.18)',
          color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <X size={19} />
      </button>

      <div style={{
        position: 'absolute', top: 22, left: 20, zIndex: 3,
        fontSize: 12.5, fontWeight: 700, color: 'rgba(255,255,255,0.65)',
      }}>
        {active + 1} / {moments.length}
      </div>

      {!isMobile && (
        <div style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', zIndex: 3, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[['up', ChevronUp, () => goTo(Math.max(active - 1, 0)), active === 0],
            ['down', ChevronDown, () => goTo(Math.min(active + 1, moments.length - 1)), active === moments.length - 1],
          ].map(([key, Icon, fn, disabled]) => (
            <button
              key={key}
              type="button"
              onClick={fn}
              disabled={disabled}
              style={{
                width: 38, height: 38, borderRadius: '50%',
                background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.16)',
                color: '#fff', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.3 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Icon size={19} />
            </button>
          ))}
        </div>
      )}

      <div
        ref={scrollRef}
        className="pmf-scroll"
        style={{
          height: '100%', overflowY: 'scroll', scrollSnapType: 'y mandatory',
          scrollbarWidth: 'none', msOverflowStyle: 'none',
        }}
      >
        {moments.map((m, i) => (
          <div key={m._id || m.id || i} ref={(el) => { slideRefs.current[i] = el; }} style={{ height: '100%' }}>
            <Slide
              moment={m}
              isActive={i === active}
              muted={muted}
              isMobile={isMobile}
              liked={likedIds.has(m._id || m.id)}
              onToggleMute={() => setMuted((v) => !v)}
              onEnded={handleEnded}
              onLike={onLike}
              onOpenFull={onOpenFull}
            />
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
};

export default PeakMomentsFeed;
